import type { CartPriceDTO, ShoppingCartDTO } from '@t/api.types';

import type { CartSummary, ChargePriceType } from './cartPricing.types';

/** Match a CartPriceDTO against a logical charge type, handling API variants. */
export const matchesPriceType = (p: CartPriceDTO, priceType: ChargePriceType): boolean => {
  if (p.priceType === 'discount') return false;
  if (priceType === 'recurring') {
    return (
      p.priceType === 'recurring' ||
      p.priceType === 'recurringCharge' ||
      (!!p.recurringChargePeriod &&
        p.recurringChargePeriod !== 'oneTime' &&
        p.priceType !== 'oneTime' &&
        p.priceType !== 'oneTimeCharge')
    );
  }
  return p.priceType === 'oneTime' || p.priceType === 'oneTimeCharge';
};

// Cart total rows prefer dutyFreeAmount with nullish coalescing — a literal 0 wins
// over a set taxIncludedAmount. The drawer's breakdown.total in pricingSummary.utils
// intentionally uses the opposite preference (taxIncluded || dutyFree); do not unify.
export const extractTotalByPriceType = (
  cart: ShoppingCartDTO | null | undefined,
  priceType: ChargePriceType
): number => {
  const match = cart?.cartTotalPrice?.find((p) => matchesPriceType(p, priceType));
  return match?.price?.dutyFreeAmount?.amount ?? match?.price?.taxIncludedAmount?.amount ?? 0;
};

// itemCount counts every cartItem entry, including coupon line items — verbatim
// characterization of the useShoppingCart original this module consolidates.
export const deriveCartSummary = (cart: ShoppingCartDTO | null | undefined): CartSummary => {
  if (!cart) return { monthlyTotal: 0, oneTimeTotal: 0, itemCount: 0 };

  return {
    monthlyTotal: extractTotalByPriceType(cart, 'recurring'),
    oneTimeTotal: extractTotalByPriceType(cart, 'oneTime'),
    itemCount: cart.cartItem?.length ?? 0,
  };
};
